/**
 * Mirrors VirtualNodeNFT.calculatePendingRewards on-chain math.
 *
 * base = time * rewardRatePerHour * tierBps / (3600 * 100)
 * hashMult  = 100 + hashPower/50
 * bwMult    = 100 + bandwidth/50
 * uptimeMult= 100 + uptime/25
 * fleetBonus= soft-capped: full +2%/node through 10, half after; max +35%
 *             (requires bandwidth > tier base and owned > 1)
 * reward = base * hash * bw * uptime * fleet / 1e8
 */

export const TIER_BPS: Record<number, number> = {
  1: 100,
  2: 250,
  3: 500,
  4: 1000,
  5: 2500,
};

export function tierBpsFor(tier: number): number {
  return TIER_BPS[tier] ?? 100;
}

export function tierBaseBandwidth(tier: number): number {
  return 10 * (tier || 1);
}

/** Matches VirtualNodeNFT fleet soft-cap (returns multiplier basis points / 100 scale). */
export function fleetBonusMultiplier(bandwidth: number, tier: number, owned: number): number {
  if (bandwidth > tierBaseBandwidth(tier) && owned > 1) {
    const effectiveOwned = owned > 10 ? 10 + Math.floor((owned - 10) / 2) : owned;
    return 100 + Math.min(effectiveOwned * 2, 35);
  }
  return 100;
}

/** Hourly ETCPOWv2 estimate (human units). Matches on-chain when rate is continuous. */
export function estimateHourlyReward(params: {
  rewardRatePerHour: number;
  tier: number;
  hashPower: number;
  bandwidth: number;
  uptime: number;
  owned?: number;
}): number {
  const { rewardRatePerHour, tier, hashPower, bandwidth, uptime, owned = 1 } = params;
  const base = (rewardRatePerHour * tierBpsFor(tier)) / 100;
  const hashMult = 100 + hashPower / 50;
  const bwMult = 100 + bandwidth / 50;
  const uptimeMult = 100 + uptime / 25;
  const fleet = fleetBonusMultiplier(bandwidth, tier, owned);
  return (base * hashMult * bwMult * uptimeMult * fleet) / 1e8;
}

/** BigInt pending estimate — same integer path as HackerDashboard fallback. */
export function estimatePendingWei(params: {
  rewardRatePerHourWei: bigint;
  timePassedSec: bigint;
  tier: number;
  hashPower: number;
  bandwidth: number;
  uptime: number;
  owned: number;
}): bigint {
  const {
    rewardRatePerHourWei,
    timePassedSec,
    tier,
    hashPower,
    bandwidth,
    uptime,
    owned,
  } = params;

  const tierBps = BigInt(tierBpsFor(tier));
  const baseReward =
    (timePassedSec * rewardRatePerHourWei * tierBps) / (3600n * 100n);
  const hashMultiplier = 100n + BigInt(hashPower) / 50n;
  const bandwidthMultiplier = 100n + BigInt(bandwidth) / 50n;
  const uptimeMultiplier = 100n + BigInt(uptime) / 25n;
  const ownedN = BigInt(Math.max(owned, 1));
  let fleetBonus = 100n;
  if (BigInt(bandwidth) > BigInt(tierBaseBandwidth(tier)) && ownedN > 1n) {
    let effectiveOwned = ownedN;
    if (ownedN > 10n) {
      effectiveOwned = 10n + (ownedN - 10n) / 2n;
    }
    let bonus = effectiveOwned * 2n;
    if (bonus > 35n) bonus = 35n;
    fleetBonus = 100n + bonus;
  }
  return (
    (baseReward * hashMultiplier * bandwidthMultiplier * uptimeMultiplier * fleetBonus) /
    100000000n
  );
}

/** Soft-read optional GameLogic fee methods (safe if ABI not deployed yet). */
export async function readOptionalProtocolFees(gameLogic: {
  reactivationFee?: () => Promise<bigint>;
  protocolFeeBps?: () => Promise<bigint | number>;
}): Promise<{
  reactivationFeeWei: bigint | null;
  protocolFeeBps: number | null;
}> {
  let reactivationFeeWei: bigint | null = null;
  let protocolFeeBps: number | null = null;

  try {
    if (typeof gameLogic.reactivationFee === 'function') {
      reactivationFeeWei = await gameLogic.reactivationFee();
    }
  } catch {
    reactivationFeeWei = null;
  }

  try {
    if (typeof gameLogic.protocolFeeBps === 'function') {
      protocolFeeBps = Number(await gameLogic.protocolFeeBps());
    }
  } catch {
    protocolFeeBps = null;
  }

  return { reactivationFeeWei, protocolFeeBps };
}
